import { useCallback, useState } from "react";

import {
  Close,
  RestartAlt,
  RotateLeft,
  RotateRight,
  Save,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors } from "@rubi/ui";
import Cropper, { Area, Point } from "react-easy-crop";

import getCroppedImg from "./getCroppedImg";

interface ImageEditorProps {
  /** Source of the image to edit (data URL or presigned URL). */
  imageUrl: string;
  /** File name to give the exported image. */
  fileName: string;
  /** Called with the edited image as a JPEG File once the user saves. */
  onSave: (file: File) => void;
  /** Called when the user cancels editing. */
  onCancel: () => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

const ASPECT_PRESETS: { key: string; label: string; value?: number }[] = [
  { key: "original", label: "Original" },
  { key: "square", label: "1:1", value: 1 },
  { key: "4:3", label: "4:3", value: 4 / 3 },
  { key: "3:4", label: "3:4", value: 3 / 4 },
  { key: "16:9", label: "16:9", value: 16 / 9 },
  { key: "9:16", label: "9:16", value: 9 / 16 },
];

const ImageEditor = ({
  imageUrl,
  fileName,
  onSave,
  onCancel,
}: ImageEditorProps) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspectPreset, setAspectPreset] = useState("original");
  const [naturalAspect, setNaturalAspect] = useState<number | undefined>(
    undefined,
  );
  const [cropAreaPixels, setCropAreaPixels] = useState<Area | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const presetValue = ASPECT_PRESETS.find((p) => p.key === aspectPreset)?.value;
  // "Original" tracks the image's natural ratio so the whole frame stays visible.
  const aspect =
    aspectPreset === "original" ? (naturalAspect ?? 4 / 3) : presetValue!;

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCropAreaPixels(areaPixels);
  }, []);

  const rotateBy = (delta: number) =>
    setRotation((r) => (((r + delta) % 360) + 360) % 360);

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setAspectPreset("original");
  };

  const handleSave = useCallback(async () => {
    if (!cropAreaPixels) return;
    setIsSaving(true);
    setError(null);
    try {
      const blob = await getCroppedImg(imageUrl, cropAreaPixels, rotation);
      const editedFile = new File([blob], fileName, { type: "image/jpeg" });
      onSave(editedFile);
    } catch (err) {
      console.error("Failed to process edited image", err);
      setError("Could not process the image. Please try again.");
      setIsSaving(false);
    }
  }, [cropAreaPixels, imageUrl, rotation, fileName, onSave]);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.common.black,
      }}
    >
      {/* Cropper surface */}
      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
        <Cropper
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          zoomSpeed={0.3}
          restrictPosition={false}
          objectFit="contain"
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          onMediaLoaded={(mediaSize) =>
            setNaturalAspect(mediaSize.naturalWidth / mediaSize.naturalHeight)
          }
        />

        {/* Close (cancel) */}
        <Tooltip title="Cancel editing" placement="left">
          <IconButton
            onClick={onCancel}
            disabled={isSaving}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: colors.common.white,
              backgroundColor: alpha(colors.common.black, 0.4),
              "&:hover": { backgroundColor: alpha(colors.common.black, 0.6) },
            }}
          >
            <Close />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Controls */}
      <Box
        sx={{
          backgroundColor: colors.text.primary,
          color: colors.common.white,
          px: 2,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {fileName && (
          <Typography
            variant="caption"
            sx={{
              color: alpha(colors.common.white, 0.7),
              maxWidth: "80vw",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Editing: {fileName}
          </Typography>
        )}

        {/* Aspect ratio */}
        <ToggleButtonGroup
          value={aspectPreset}
          exclusive
          size="small"
          onChange={(_, next) => next && setAspectPreset(next)}
          sx={{
            flexWrap: "wrap",
            justifyContent: "center",
            "& .MuiToggleButton-root": {
              color: alpha(colors.common.white, 0.7),
              borderColor: alpha(colors.common.white, 0.23),
              textTransform: "none",
              px: 1.5,
            },
            "& .Mui-selected": {
              color: "white !important",
              backgroundColor: "rgba(255,255,255,0.16) !important",
            },
          }}
        >
          {ASPECT_PRESETS.map((preset) => (
            <ToggleButton key={preset.key} value={preset.key}>
              {preset.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Zoom + rotation sliders */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            width: "100%",
            maxWidth: 640,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 220,
            }}
          >
            <ZoomOut fontSize="small" />
            <Slider
              aria-label="Zoom"
              value={zoom}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.05}
              onChange={(_, value) =>
                setZoom(Array.isArray(value) ? value[0] : value)
              }
              sx={{ color: colors.common.white }}
            />
            <ZoomIn fontSize="small" />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 220,
            }}
          >
            <Tooltip title="Rotate left 90°">
              <IconButton
                onClick={() => rotateBy(-90)}
                size="small"
                sx={{ color: colors.common.white }}
              >
                <RotateLeft />
              </IconButton>
            </Tooltip>
            <Slider
              aria-label="Rotation"
              value={rotation}
              min={0}
              max={360}
              step={1}
              onChange={(_, value) =>
                setRotation(Array.isArray(value) ? value[0] : value)
              }
              sx={{ color: colors.common.white }}
            />
            <Tooltip title="Rotate right 90°">
              <IconButton
                onClick={() => rotateBy(90)}
                size="small"
                sx={{ color: colors.common.white }}
              >
                <RotateRight />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Typography variant="caption" sx={{ color: colors.error.light }}>
            {error}
          </Typography>
        )}

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            width: "100%",
            maxWidth: 640,
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={handleReset}
            disabled={isSaving}
            startIcon={<RestartAlt />}
            sx={{
              color: alpha(colors.common.white, 0.8),
              textTransform: "none",
            }}
          >
            Reset
          </Button>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isSaving}
              sx={{
                color: colors.common.white,
                borderColor: alpha(colors.common.white, 0.5),
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || !cropAreaPixels}
              startIcon={
                isSaving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Save />
                )
              }
              sx={{ textTransform: "none" }}
            >
              {isSaving ? "Saving…" : "Save as new attachment"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ImageEditor;
